import { v4 as uuidV4 } from 'uuid'

import graphql from 'api/graphql'
import { doAutoRedirect } from 'actions/autoRedirect'
import { updateCustomerById } from 'ducks/customers/actions'
import { doShowSnackbar } from 'actions/snackbar'
import {
  doSendChangeset,
  doSearchUsers,
  doClearUserSuggestion,
} from 'actions/tickets'
import {
  doOpenTicketPage,
  doOpenReplyPage,
  doOpenNotePage,
} from 'actions/pages'
import { startTimer as startUndoTimer } from 'actions/undoSend'

import { newTicketChangesetDefinition } from 'api/tickets'

import * as types from 'constants/action_types'
import * as draftActionTypes from 'ducks/drafts/types'

import {
  oauthTokenSelector,
  selectCurrentFolderId,
  selectDeviceTypeIsMobile,
  selectCurrentTicketListRouteAction,
} from 'selectors/app'
import {
  selectCurrentUser,
  selectPreferences,
  selectPrefersClassicView,
  selectPrefersOpenFolderOnTicketReply,
  selectPrefersOpenFolderOnNoteAdd,
} from 'ducks/currentUser/selectors'
import { selectAgentsById } from 'selectors/agents/base'
import { getTwitterHandle } from 'selectors/currentChangesets'
import { selectFolderIdsMatchingDraftForCurrentUser } from 'selectors/folders'
import { selectCurrentTicketSearchQueryObject } from 'selectors/search'
import {
  selectCurrentCustomer,
  selectCurrentTicket,
  selectCurrentTicketId,
  selectTicket,
} from 'selectors/tickets'
import {
  selectTicketDraft,
  selectUndecoratedTicketReplyDrafts,
  selectReplyDraft,
  selectReplyDraftForTicketId,
  selectReplyDraftPreflight,
  selectNoteDraft,
} from 'ducks/drafts'
import { selectNextTicketIdInList } from 'selectors/ticket_list'

import { messagePayload } from 'optimistic/message'
import { notePayload } from 'optimistic/note'

import { last } from 'util/arrays'
import { ticketHasAttachments } from 'util/draft'
import { debounce, runOnNextTick } from 'util/functions'
import { queryParams } from 'util/params'
import { withOptimist, batchActions } from 'util/redux'
import { isHtmlEmpty } from 'util/strings'
import { isTwitter, isFacebook, isNewTicket } from 'util/ticket'
import { byEmailAddressQuery as customersByEmailAddressQuery } from 'ducks/customers/api'

import debug from 'util/debug'

function logError(message, metaData) {
  debug(message, metaData)

  if (bugsnagClient) {
    bugsnagClient.notify(new Error(`${message}`), {
      severity: 'error',
      context: metaData.context,
      metaData: {
        meta: {
          ...metaData,
          path: window.location.pathname,
        },
      },
    })
  }
}

// Always goes to the store to get the saved draft, and sends that.
// This is to encourage us to always keep track of our draft updates in redux,
// which simplifies troubleshooting
function doSaveReplyDraft(ticketId) {
  return (dispatch, getState) => {
    const state = getState()
    const draft = selectReplyDraftForTicketId(state, ticketId)

    dispatch({
      type: draftActionTypes.SAVE_REPLY_DRAFT_REQUEST,
      data: { draft, ticketId },
    })

    const mutation = `
      mutation UpdateDraft(
        $ticketId: String,
        $body: String,
        $quotedText: String
      ) {
        createTicketDraft(
          ticketId: $ticketId,
          body: $body,
          quotedText: $quotedText
        ) {
          body
        }
      }
    `

    const variables = {
      ticketId,
      body: draft ? draft.body : '',
    }

    if (draft && typeof draft.quotedText === 'string') {
      variables.quotedText = draft.quotedText
    }

    const token = oauthTokenSelector(state)
    return graphql(token, mutation, variables)
      .then(response => {
        dispatch({
          type: draftActionTypes.SAVE_REPLY_DRAFT_COMPLETE,
          data: { response },
        })
      })
      .catch(err => {
        logError('Draft save failed', {
          ticketId,
          err,
          context: 'doSaveReplyDraft',
        })
        dispatch({
          type: draftActionTypes.SAVE_REPLY_DRAFT_FAIL,
          data: { err, draft, ticketId },
        })
        dispatch(
          doShowSnackbar('Oops. We could not save your draft. ', {
            mobileMessage: 'Oops. We could not save your draft.',
            duration: 5000,
          })
        )
      })
  }
}

// DEPRECATED. Use specific draft type updaters instead like updateReplyDraft
// or updateNoteDraft.
export function doUpdateDraft(draft) {
  return (dispatch, getState) => {
    debug('DEPRECATED: use doUpdate[Reply/Note]Draft instead')
    const state = getState()
    const ticketId = draft ? draft.ticketId : selectCurrentTicketId(state)
    if (draft && draft.isNote) {
      return dispatch(updateNoteDraft(ticketId, draft))
    }
    return dispatch(updateReplyDraft(ticketId, draft))
  }
}

// Our old doUpdate[Reply]Draft
function updateReplyDraft(ticketId, draftDiff, { skipSave } = {}) {
  return (dispatch, getState) => {
    if (!ticketId) return false

    const state = getState()
    const currentUser = selectCurrentUser(state) // only for search reducer?
    const currentDraft = selectUndecoratedTicketReplyDrafts(state)[ticketId]
    const diff = { ...draftDiff }

    if (diff) {
      // Sanitize body before sending to server. Empty drafts are usually
      // composed of <p><br></p> but we also want to catch things like <p><br
      // /> </p>
      ;['body', 'quotedText'].forEach(key => {
        if (diff[key]) {
          const sanitized = diff[key]
            .replace(/<\/*(p|br)[^>]*>/g, '')
            .replace(/^\s+/, '')
            .replace(/\s+$/, '')

          if (sanitized === '') diff[key] = ''
        }
      })
    }

    dispatch({
      type: draftActionTypes.UPDATE_LOCAL_REPLY_DRAFT,
      data: {
        ticketId,
        diff,
        currentUserId: currentUser.id,
        currentDraftBody: currentDraft ? currentDraft.body : '',
        draftFolderIds: selectFolderIdsMatchingDraftForCurrentUser(state),
      },
    })

    // Note we *DO* usually want to send empty drafts to the server so they are
    // cleared properly. But do not save new ticket drafts to the server
    if (skipSave || ticketId === 'new') return Promise.resolve(true)

    return dispatch(doSaveReplyDraft(ticketId))
  }
}

export function doDeleteReplyDraft(ticketId) {
  return dispatch => {
    const draft = {
      ticketId,
      body: '',
    }

    // NOTE: this updateReplyDraft actually deletes it from the server...
    dispatch(updateReplyDraft(ticketId, draft))
    dispatch(doInvalidateReplyDraft(ticketId))
  }
}

function doInvalidateReplyDraft(ticketId) {
  return {
    type: draftActionTypes.INVALIDATE_REPLY_DRAFT,
    data: {
      ticketId,
    },
  }
}

export function doInvalidateNoteDraft(ticketId) {
  return {
    type: draftActionTypes.INVALIDATE_NOTE_DRAFT,
    data: {
      ticketId,
    },
  }
}

// A curry-able wrapper around updateReplyDraft that encourages smaller actions
// that describe only the changes to be made to a draft.
//
// We used to simply build the new draft object inside the action
// creator with something like;
//
//    doUpdateReplyDraft({...draft, [propName]: text })
//
//  it was super opaque as to what was happening and which prop was being
//  updated at any given point in time. Thats why prefer specific
//  actions that describe single prop changes.
//
// This also help remind readers that that the canonical draft is always stored
// in redux. We should avoid caching/manipulating drafts on the fly - e.g. inside
// react components, as we have run into consistency problems with stale copies
export const doUpdateReplyDraft = (ticketId, propName, value) => dispatch => {
  return dispatch(updateReplyDraft(ticketId, { [propName]: value }))
}

// to debounce a thunk, the inner function needs to be the same every time.
const innerUpdateReplyDraftDelayed = debounce(
  (ticketId, propName, value, dispatch) => {
    return dispatch(doUpdateReplyDraft(ticketId, propName, value))
  },
  500,
  false
)

// We ask the Editor to give us hints about the before & after content. If we
// detect the user began editing (firstEdit) or the user cleared the editor
// contents (deletedAll), then to avoid 1. delays in enabling/disabling Send
// button and 2. Immediately clear drafts on delete (just in case the user
// closes the form before a debounce update applies - otherwise the draft might
// stick.)
// All very convoluted. The fix is to be able to call update (local) draft (sans
// API save request) on every keystroke. But it runs like a dog...
export const doUpdateReplyDraftDelayed = (
  ticketId,
  propName,
  value,
  { firstEdit, deletedAll, skipSave } = {}
) => dispatch => {
  if (firstEdit || deletedAll) {
    // We immediately reflect the change locally
    // GR: unfortunately this is an expensive operation - too much rendering ?
    // If we could make this update cheaper (and separate local draft update
    // from the API save), we could do the local draft update on every
    // keystroke.
    // pnagy: when deletedAll is true and we empty the field with an immediate update
    // we need to make sure to cancel any previous update on the way to prevent stucking drafts
    innerUpdateReplyDraftDelayed.cancel()
    return dispatch(
      updateReplyDraft(ticketId, { [propName]: value }, { skipSave })
    )
  }

  // otherwise our usual debounced update and save.
  return innerUpdateReplyDraftDelayed(ticketId, propName, value, dispatch)
}

export const handleUpdateReplyDraftDelayed = (
  dispatch,
  ticketId,
  propName,
  value,
  { firstEdit, deletedAll, skipSave } = {}
) => {
  if (firstEdit || deletedAll) {
    // We immediately reflect the change locally
    // GR: unfortunately this is an expensive operation - too much rendering ?
    // If we could make this update cheaper (and separate local draft update
    // from the API save), we could do the local draft update on every
    // keystroke.
    // pnagy: when deletedAll is true and we empty the field with an immediate update
    // we need to make sure to cancel any previous update on the way to prevent stucking drafts
    innerUpdateReplyDraftDelayed.cancel()
    return runOnNextTick(() => {
      dispatch(updateReplyDraft(ticketId, { [propName]: value }, { skipSave }))
    })
  }

  // otherwise our usual debounced update and save.
  return innerUpdateReplyDraftDelayed(ticketId, propName, value, dispatch)
}

function updateNoteDraft(ticketId, draft) {
  return {
    type: draftActionTypes.UPDATE_NOTE_DRAFT,
    data: {
      ticketId,
      draft,
    },
  }
}

// A curry-able wrapper around updateNoteDraft that encourages smaller actions
// that describe only the changes to be made to a draft.
export const doUpdateNoteDraft = (ticketId, propName, value) => dispatch => {
  return dispatch(updateNoteDraft(ticketId, { [propName]: value }))
}

const innerUpdateNoteDraftDelayed = debounce(
  (ticketId, propName, value, dispatch) => {
    return dispatch(doUpdateNoteDraft(ticketId, propName, value))
  },
  500,
  false
)

// to debounce a thunk, the inner function needs to be the same every time.
export const doUpdateNoteDraftDelayed = (
  ticketId,
  propName,
  value,
  { firstEdit, deletedAll } = {}
) => dispatch => {
  if (firstEdit || deletedAll) {
    // We immediately reflect the change locally
    return dispatch(doUpdateNoteDraft(ticketId, propName, value))
  }
  return innerUpdateNoteDraftDelayed(ticketId, propName, value, dispatch)
}

export function doUpdateNoteDraftToRecipient(ticketId, recipient) {
  return {
    type: draftActionTypes.UPDATE_NOTE_DRAFT_RECIPIENT,
    data: {
      ticketId,
      recipient,
    },
  }
}

const innerUpdateNoteDraftToRecipientDelayed = debounce(
  (ticketId, recipient, dispatch) => {
    return dispatch(doUpdateNoteDraftToRecipient(ticketId, recipient))
  },
  250,
  false
)

// to debounce a thunk, the inner function needs to be the same every time.
export const doUpdateNoteDraftToRecipientDelayed = (
  ticketId,
  recipient
) => dispatch =>
  innerUpdateNoteDraftToRecipientDelayed(ticketId, recipient, dispatch)

export function doRemoveNoteDraftToRecipient(ticketId) {
  return updateNoteDraft(ticketId, { to: undefined })
}

export function doUpdateNoteDraftBody(ticketId, body) {
  return {
    type: draftActionTypes.NOTE_DRAFT_BODY_CHANGED,
    data: { ticketId, body },
  }
}

export function doUpdateReplyDraftRecipients(ticketId, recipients) {
  return {
    type: draftActionTypes.UPDATE_REPLY_DRAFT_RECIPIENTS,
    data: {
      ticketId,
      recipients,
    },
  }
}

function doUpdateMultipleDraftRecipients(ticketId, recipients) {
  return {
    type: draftActionTypes.UPDATE_MULTIPLE_DRAFT_RECIPIENTS,
    data: {
      ticketId,
      recipients,
    },
  }
}

export function doAddDraftRecipients(ticketId, recipients) {
  return {
    type: draftActionTypes.ADD_REPLY_DRAFT_RECIPIENTS,
    data: {
      ticketId,
      recipients,
    },
  }
}

export function doRemoveDraftRecipients(ticketId, recipients) {
  return {
    type: draftActionTypes.REMOVE_REPLY_DRAFT_RECIPIENTS,
    data: {
      ticketId,
      recipients,
    },
  }
}

export function doUpdateNoteFormHeight(height) {
  return {
    type: types.UPDATE_NOTE_FORM_HEIGHT,
    data: {
      height,
    },
  }
}

export function doUpdateRecipientQuery(query) {
  return {
    type: draftActionTypes.UPDATE_REPLY_DRAFT_RECIPIENT_QUERY,
    data: {
      query,
    },
  }
}

export const doSelectRecipient = (type, recipient) => {
  return {
    type: draftActionTypes.SELECT_DRAFT_RECIPIENT,
    data: {
      [type]: recipient,
    },
  }
}

export const doClearSelectedRecipient = type => {
  return {
    type: draftActionTypes.CLEAR_SELECTED_DRAFT_RECIPIENT,
    data: {
      type,
    },
  }
}

const MIN_RECIPIENT_QUERY_LENGTH = 1
function searchRecipients(dispatch, getState, type, query) {
  // start batch actions and clear any previously selected recipients
  const actions = [
    { type: types.SEARCH_RECIPIENT_REQUEST },
    doClearSelectedRecipient(type),
    doClearUserSuggestion(),
    doUpdateRecipientQuery(query),
  ]
  if (query.length < MIN_RECIPIENT_QUERY_LENGTH) {
    actions.push({ type: types.SEARCH_RECIPIENT_COMPLETE })
    dispatch(batchActions(actions))
    return Promise.resolve()
  }
  dispatch(batchActions(actions))
  // kick off a user search
  return dispatch(doSearchUsers(query)).then(() => {
    dispatch({ type: types.SEARCH_RECIPIENT_COMPLETE })
  })
}

// we only allow one search globally
let searchTimeoutId
const searchDelay = 100

export function doSearchRecipients(type, term, withoutIds) {
  return (dispatch, getState) => {
    if (searchTimeoutId) clearTimeout(searchTimeoutId)
    return new Promise(resolve => {
      const delay = term.length < MIN_RECIPIENT_QUERY_LENGTH ? 0 : searchDelay
      searchTimeoutId = setTimeout(
        () =>
          resolve(searchRecipients(dispatch, getState, type, term, withoutIds)),
        delay
      )
    })
  }
}

// Closed tickets become opened. All others toggle to closed
export const doToggleDraftState = () => {
  return (dispatch, getState) => {
    const state = getState()
    const draft = selectTicketDraft(state)
    let newState = 'closed'
    if (draft.state === 'closed') newState = 'opened'
    const newDraft = { ...draft, state: newState }
    dispatch(doUpdateDraft(newDraft))
  }
}

const handleFailedMessageSend = dispatch => err => {
  logError('Failed sending message', {
    err,
    context: 'handleFailedMessageSend',
  })
  dispatch(
    doShowSnackbar(
      'Oops. Your message could not be sent. Please try again in a moment'
    )
  )
  throw err
}

function isUndoable(draft, ticket) {
  if (!draft) return false
  if (draft.isForwarding && draft.isNote) return false
  if (isFacebook(ticket) || isTwitter(ticket)) return false
  return true
}

function sendReplyDraft(ticketDraft, pageActionFn = doOpenTicketPage) {
  return (dispatch, getState) => {
    const state = getState()
    const draft = ticketDraft || selectReplyDraft(state)

    if (!draft) return false
    // for any reply except send & snooze, (i.e. send & close/open), we need to
    // explicitly clear the snooze too.
    if (!draft.snoozeUntil) draft.removeSnooze = true

    const ticketId = ticketDraft.id
    const { undo_send_delay: delaySeconds } = selectPreferences(state)
    const changesetId = uuidV4()
    const prefersUndo = delaySeconds
    const undoable =
      prefersUndo && isUndoable(draft, selectTicket(state, ticketId))
    const nextTicketId = selectNextTicketIdInList(state) || null
    const classicView = selectPrefersClassicView(state)

    const optimisticData = messagePayload(
      state,
      ticketId,
      draft,
      changesetId,
      selectCurrentFolderId(state),
      selectCurrentTicketSearchQueryObject(state)
    )

    let action
    if (isNewTicket(ticketDraft)) {
      action = doSendChangeset(null, draft, { optimisticData, changesetId })
    } else {
      action = doSendChangeset(ticketId, draft, {
        optimisticData,
        changesetId,
        // GR: Our SEND_REPLY reducers will handle (optimistic) deleting, and
        // restoration of drafts (on failure). We dont want/need doSendChangeset
        // to delete drafts for us. I recommend deprecating preserveDraft.
        preserveDraft: true, // disable, so we have full control here.
        // we want control over when to auto-navigate away - chiefly we'd like
        // the user to see the undo send button before it auto-navs away.
        // GR: I also recommend deprecating this.
        // NOTE (mbunsch) this line, when set to true, completely wrecks auto-advance
        // I need to bring it back, especially that now undo send is in a toast
        disableNavigateToFolder: !!classicView, // NOTE (jscheel): Normally sending false, but classicView needs to send true
      })
    }

    dispatch(doShowSnackbar('Sending your reply...'))
    const promise = dispatch(
      withOptimist(action, {
        payload: { draft, ticketId, changesetId },
        actions: {
          pending: draftActionTypes.SEND_REPLY,
          fulfilled: draftActionTypes.SEND_REPLY_COMPLETE,
          rejected: draftActionTypes.SEND_REPLY_FAIL,
        },
      })
    )
      .then(res => {
        if (!undoable || selectDeviceTypeIsMobile(state)) {
          dispatch(
            doShowSnackbar('Reply sent', {
              link: `/tickets/${ticketId}`,
              linkText: 'Go back to conversation',
            })
          )
        }
        if (undoable) {
          runOnNextTick(() => {
            dispatch(startUndoTimer(changesetId, ticketId, draft))
          })
        }

        // GR: Not sure why the API doesnt handle this for us!
        dispatch(doDeleteReplyDraft(ticketId))

        return Promise.resolve(res)
      })
      .catch(err => {
        // At this point, the request failed, so reopen the reply form
        dispatch(pageActionFn(ticketId))
        throw err
      })
      .catch(err => {
        handleFailedMessageSend(dispatch)(err)
      })

    // NOTE (jscheel): Execute on next cycle so everything else runs before
    // we navigate away.
    if (classicView) {
      const shouldOpenFolder = selectPrefersOpenFolderOnTicketReply(state)
      const routeAction = selectCurrentTicketListRouteAction(state)
      if (shouldOpenFolder && routeAction) {
        dispatch(routeAction)
      }
    } else if (nextTicketId && !undoable) {
      dispatch(doAutoRedirect(ticketId, nextTicketId))
    }

    return promise
  }
}

// DEPRECATED
export function doSendMessage(draft) {
  return dispatch => {
    // This API allows/encourages us to maninpulate drafts right before sending
    // them. This makes it harder to debug. The newer API (which only takes a
    // ticketId, and selects the draft for you) forces you to do your updates in
    // redux first.
    debug('DEPRECATED: use doSendReplyDraft instead')
    return dispatch(sendReplyDraft(draft, doOpenReplyPage))
  }
}

// helper fn to build newer send actions - ones that only take a ticketId (not a
// full draft)
//
// Usage: pass a selectDraft function e.g. selectReplyDraft, and a fn that sends
// the draft (e.g. doSendReplyDraft). It will return an action creator that selects
// the 'draft' using the selectDraft fn, and then dispatch the 'send' fn
// eslint-disable-next-line no-unused-vars
const selectAndSend = (
  selectDraftFn,
  checkIds = false
) => sendActionFn => ticketId => {
  return (dispatch, getState) => {
    // While it works for now, at some point we'll select using given ticketId.
    const draft = selectDraftFn(getState())

    // unlikely, but needed while still use a 'current' ticket selector...
    if (checkIds && ticketId !== draft.id) {
      debug('Not sending draft: id mismatch')
      return Promise.resolve(false)
    }

    return dispatch(sendActionFn(draft))
  }
}

// helper fn to build the composite update & send actions
const updateAndSendReply = sendActionFn => (ticketId, changes) => dispatch => {
  // if any debounced (body) updates on their way, cancel 'em.
  innerUpdateReplyDraftDelayed.cancel()

  // we want to do a local update, but since we are immediately sending it, skip
  // the usual draft save
  return dispatch(updateReplyDraft(ticketId, changes, { skipSave: true })).then(
    () => {
      return dispatch(sendActionFn(ticketId))
    }
  )
}

//
// Emails/Replies
//

// used by new email and reply forms
const selectAndSendReplyDraft = (sendActionFn, pageActionFn) => ticketId => {
  return (dispatch, getState) => {
    // While it works for now, at some point we'll select using given ticketId.
    const draft = selectReplyDraftPreflight(getState())

    // unlikely, but needed while still use a 'current' ticket selector...
    if (ticketId !== draft.id) {
      debug('Not sending reply draft: id mismatch')
      return Promise.resolve(false)
    }

    return dispatch(sendActionFn(draft, pageActionFn))
  }
}

const doSendReplyDraft = selectAndSendReplyDraft(sendReplyDraft)

export const doUpdateAndSendReplyDraft = updateAndSendReply(doSendReplyDraft)

function sendNewEmail(draft) {
  return dispatch => {
    if (!draft) return Promise.resolve(null)

    const draftId = draft.id // 'new'
    // const { undo_send_delay: delaySeconds } = selectPreferences(state)
    // We have issue with undoSend on new tickets, so we're disabling it for now
    const delaySeconds = null
    const changesetId = uuidV4()

    // TODO: Multiple "to"s
    const lastRecipient = last(draft.to)
    if (lastRecipient.id)
      dispatch(updateCustomerById(lastRecipient.id, lastRecipient))

    return dispatch(
      doSendChangeset(null, draft, {
        changesetId,
        preserveDraft: true,
        mutation: newTicketChangesetDefinition,
        disableNavigateToFolder: true,
      })
    )
      .then(responseData => {
        const ticketId = responseData.ticket.id

        dispatch(doDeleteReplyDraft(draftId))

        if (delaySeconds) {
          dispatch(startUndoTimer(delaySeconds, changesetId, ticketId))
        } else {
          dispatch(doShowSnackbar('Reply sent'))
        }

        dispatch(doOpenTicketPage(ticketId))

        return Promise.resolve(ticketId)
      })
      .catch(handleFailedMessageSend(dispatch))
  }
}

// DEPRECATED
export function doSendNewMessage(draft) {
  return dispatch => {
    // This API allows/encourages us to maninpulate drafts right before sending
    // them. This makes it harder to debug. The newer API (which only takes a
    // ticketId, and selects the draft for you) forces you to do your updates in
    // redux first.
    debug('DEPRECATED: use doSendNewEmail instead')
    return dispatch(sendNewEmail(draft))
  }
}

const doSendNewEmail = selectAndSendReplyDraft(sendNewEmail)

export const doUpdateAndSendNewEmail = updateAndSendReply(doSendNewEmail)

//
// Comments/Notes
//
function sendNote(ticketId, draft) {
  return (dispatch, getState) => {
    if (!draft || (isHtmlEmpty(draft.body) && !ticketHasAttachments(draft))) {
      return Promise.resolve(false)
    }

    const state = getState()
    const changesetId = uuidV4()
    const optimisticData = notePayload(
      state,
      ticketId,
      draft,
      changesetId,
      selectCurrentFolderId(state),
      selectCurrentTicketSearchQueryObject(state)
    )
    const classicView = selectPrefersClassicView(state)

    dispatch(doOpenTicketPage(ticketId))

    const promise = dispatch(
      withOptimist(
        doSendChangeset(ticketId, draft, {
          optimisticData,
          disableNavigateToFolder: !!classicView, // NOTE (jscheel): Normally sending false, but classicView needs to send true
        }),
        {
          payload: { draft, ticketId },
          actions: {
            pending: draftActionTypes.SEND_NOTE,
            fulfilled: draftActionTypes.SEND_NOTE_COMPLETE,
            rejected: draftActionTypes.SEND_NOTE_FAIL,
          },
        }
      )
    )
      .then(() => {
        dispatch(
          batchActions([
            doInvalidateNoteDraft(draft.id),
            updateNoteDraft(draft.id, null),
          ])
        )
      })
      .catch(err => {
        // At this point, the request failed, so reopen the form
        dispatch(doOpenNotePage(ticketId))
        throw err
      })
      .catch(handleFailedMessageSend(dispatch))
    const shouldOpenFolder = selectPrefersOpenFolderOnNoteAdd(state)
    if (shouldOpenFolder) {
      const routeAction = selectCurrentTicketListRouteAction(state)
      if (routeAction) {
        dispatch(routeAction)
      }
    }
    return promise
  }
}

export function doSendNote(ticketId, draft) {
  return dispatch => {
    debug('DEPRECATED: use doSendComment instead')
    return dispatch(sendNote(ticketId, draft))
  }
}

// used by log convo and note forms
const selectAndSendComment = sendActionFn => ticketId => {
  return (dispatch, getState) => {
    // While it works for now, at some point we'll select using given ticketId.
    const draft = selectNoteDraft(getState())
    return dispatch(sendActionFn(ticketId, draft))
  }
}

// this gets the setStateAndSend cruft out of our components.
const updateAndSendComment = sendActionFn => (
  ticketId,
  changes
) => dispatch => {
  // if any debounced (body) updates on their way, cancel 'em.
  innerUpdateNoteDraftDelayed.cancel()

  dispatch(updateNoteDraft(ticketId, changes)) // doesnt return a promise!
  return dispatch(sendActionFn(ticketId))
}

const doSendComment = selectAndSendComment(sendNote)

export const doUpdateAndSendComment = updateAndSendComment(doSendComment)

function logNewConversation(_ignore, draft) {
  return dispatch => {
    if (!draft) return Promise.resolve(null)
    const draftId = draft.id // 'new'
    const changesetId = uuidV4()
    // force it to look like a new note!
    const payload = { ...draft, ticketId: undefined, isNote: true }

    dispatch({ type: types.LOG_NEW_CONVERSATION, data: { payload } })

    return dispatch(
      doSendChangeset(null, payload, {
        changesetId,
        mutation: newTicketChangesetDefinition,
        disableNavigateToFolder: true,
      })
    )
      .then(data => {
        const { ticket } = data
        const ticketId = ticket.id
        const actions = [
          {
            type: types.NEW_CONVERSATION_LOGGED,
            data: {
              ticket,
            },
          },
          doInvalidateNoteDraft(draftId),
          updateNoteDraft(draftId, null),
          doShowSnackbar('Conversation logged'),
        ]
        dispatch(batchActions(actions))
        dispatch(doOpenTicketPage(ticketId))
        return Promise.resolve(ticketId)
      })
      .catch(handleFailedMessageSend(dispatch))
  }
}

const doLogNewConversation = selectAndSendComment(logNewConversation)

export const doUpdateAndLogNewConversation = updateAndSendComment(
  doLogNewConversation
)

export function doAssignAgentToDraft(ticketId, agentId = null) {
  return function assignAgentToDraft(dispatch, getState) {
    const state = getState()
    const agentsById = selectAgentsById(state)
    const agent = agentsById[agentId] || null
    const draft = selectTicketDraft(state)
    const newDraft = { ...draft, assignee: agent }
    // return a promise so we're consistent with other async modals actions
    // e.g. doBulkAssignToAgent
    return Promise.resolve(dispatch(doUpdateDraft(newDraft)))
  }
}

export function doAssignGroupToDraft(ticketId, groupId = null) {
  return function assignGroupToDraft(dispatch, getState) {
    const state = getState()
    const draft = selectTicketDraft(state)
    const newDraft = { ...draft, assigned_group_id: groupId }
    // return a promise so we're consistent with other async modal actions e.g.
    // doBulkAssignToGroup
    return Promise.resolve(dispatch(doUpdateDraft(newDraft)))
  }
}

export function doUpdateDraftWithTwitterUsername() {
  return function addTwitterUsername(dispatch, getState) {
    const state = getState()
    const draft = selectTicketDraft(state)
    const customer = selectCurrentCustomer(state)
    const body = `${getTwitterHandle(customer)}&nbsp;`
    const newDraft = { ...draft, body }
    dispatch(doUpdateDraft(newDraft))
  }
}

export const doUpdateDraftMailbox = mailboxId => {
  return (dispatch, getState) => {
    const state = getState()
    const draft = selectTicketDraft(state)
    const newDraft = { ...draft, mailboxId }
    dispatch(doUpdateDraft(newDraft))
  }
}

export function doUpdateDraftQuotedText(quotedText) {
  return (dispatch, getState) => {
    const state = getState()
    const draft = selectTicketDraft(state)
    const newDraft = { ...draft, quotedText }
    dispatch(doUpdateDraft(newDraft))
  }
}

export function doToggleDraftQuotedText() {
  return (dispatch, getState) => {
    const state = getState()
    const draft = selectTicketDraft(state)
    const newDraft = {
      ...draft,
      quotedText: draft.quotedText === undefined ? '' : undefined,
    }
    dispatch(doUpdateDraft(newDraft))
  }
}

export function doUpdateReplyDraftRecipientsFromUrl() {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const params = queryParams()
    const recipientEmailAddresses = []

    if (params.to) {
      recipientEmailAddresses.push(params.to)
    }

    if (params.cc) {
      params.cc.split(',').forEach(email => recipientEmailAddresses.push(email))
    }

    if (params.bcc) {
      params.bcc
        .split(',')
        .forEach(email => recipientEmailAddresses.push(email))
    }

    return graphql(
      token,
      customersByEmailAddressQuery(
        `
        id
        name
        email
      `
      ),
      { emailAddresses: recipientEmailAddresses.join(',') }
    ).then(res => {
      const { data } = res.json
      const customersByEmail = {}
      const recipients = {}

      data.customers.records.forEach(recipient => {
        customersByEmail[recipient.email] = recipient
      })

      if (params.to) {
        if (customersByEmail[params.to]) {
          recipients.to = [
            {
              ...customersByEmail[params.to],
              type: 'Customer',
            },
          ]
        } else {
          recipients.to = [{ email: params.to }]
        }
      }

      if (params.cc) {
        recipients.cc = params.cc.split(',').map(email => {
          if (customersByEmail[email]) {
            return { ...customersByEmail[email], type: 'Customer' }
          }
          return { email }
        })
      }

      if (params.bcc) {
        recipients.bcc = params.bcc.split(',').map(email => {
          if (customersByEmail[email]) {
            return { ...customersByEmail[email], type: 'Customer' }
          }
          return { email }
        })
      }

      dispatch(doUpdateMultipleDraftRecipients('new', recipients))
    })
  }
}

export function doUpdateDraftFieldsFromUrl() {
  return dispatch => {
    const params = queryParams()
    const ticketFields = { ticketId: 'new' }

    if (params.subject) {
      ticketFields.title = params.subject
    }

    if (params.body) {
      ticketFields.body = params.body
    }

    if (params.mailbox) {
      ticketFields.mailbox = params.mailbox
    }

    dispatch(doUpdateDraft(ticketFields))
  }
}

// This will initialize the draft object in redux using the given draft.
//
// Since we construct a draft from a ticket using selectors, we pass that
// selected draft into a component, then when the component is mounted, we kick
// this off to hydrate the ticketDraft store.
//
// NOTE: Only call this once on mount! Otherwise you may cause problems with
// instantiating out-of-date drafts.
//
// Kinda jank, but the best we got right now...
//
// A more foolproof way would be to reduce on the _REPLY_PAGE action and build
// the complete draft in there - but thats tricky given how our store is
// structured (you need access to things like agentsById, and selectors tend to
// be 'easier', but result in long-winded explanations, such as this.)
export function doInitializeDraft(ticketId, draft) {
  return (dispatch, getState) => {
    const ticket = selectCurrentTicket(getState())

    // This shouldnt happen but it is, so there is a real problem with our
    // implementation. Either way, we really really want to avoid building a
    // draft from a different ticket.
    if (ticketId !== ticket.id) {
      const error = 'Draft init: Chickening out: ticket ids dont match.'
      debug(error)
      // bugsnag error sending intentionally removed to reduce noise.
      return false
    }

    return dispatch({
      type: draftActionTypes.INIT_LOCAL_REPLY_DRAFT,
      data: {
        ticketId,
        draft,
        ticket,
      },
    })

    // there is no point saving the draft to the server as the user has
    // done nothing to it at this point, and whatever draft state we have can be
    // derived from the ticket.
  }
}
